import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/expo-pilot-logo.png";
import axiosInstance from "../../utils/api";
import { FiEye, FiEyeOff, FiMail, FiLock, FiArrowRight } from "react-icons/fi";

const redirectByRole = (user) => {
  switch (user.role) {
    case "admin":
      return "/dashboard";
    case "floor-manager":
      return "/staff";
    case "security":
      return "/scan";
    case "hse":
      return "/hse";
    case "cleaning":
      return "/cleaning";
    case "sub_admin":
      return `/event/${user.event}/dashboard`;
    case "orga": {
      const access = user.accesses || [];
      if (access.includes("dashboard")) return `/event/${user.event}/dashboard`;
      if (access.includes("stands")) return `/event/${user.event}/stand-list`;
      if (access.includes("retro-plan")) return `/event/${user.event}/retro-plan`;
      if (access.includes("cleaning")) return `/event/${user.event}/cleaning`;
      if (access.includes("security")) return `/event/${user.event}/security`;
      if (access.includes("badging")) return `/event/${user.event}/badging`;
      return "/no-access";
    }
    default:
      return null;
  }
};


const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axiosInstance.post("/auth/login", {
        email,
        password,
      });

      const { accessToken, refreshToken, user } = res.data;

      if (!accessToken || !refreshToken || !user) {
        throw new Error("Invalid credentials");
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("event", user.event);

      navigate(redirectByRole(user));

    } catch (err) {
      if (err?.response?.status === 429) {
        setError("Too many login attempts. Please wait a moment and try again.");
      } else if (err?.response?.status === 401) {
        setError("Invalid email or password");
      } else if (!err?.response) {
        setError("Unable to reach the server. Please check your connection.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-blue-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-slate-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b15_1px,transparent_1px),linear-gradient(to_bottom,#1e293b15_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 text-center lg:text-left px-6 lg:px-12">
              <div className="inline-block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-slate-700 blur-2xl opacity-50 rounded-full"></div>
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                    <img src={logo} alt="Expo pilot Logo" className="w-48 h-auto" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-black text-white tracking-tight">
                  Events
                  <span className="block mt-2 bg-gradient-to-r from-blue-400 via-blue-500 to-slate-300 bg-clip-text text-transparent">
                    Manager
                  </span>
                </h1>
                <p className="text-xl text-slate-300 leading-relaxed max-w-md">
                  Your all-in-one solution for managing events, stands, halls, tents, and everything in between.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center px-6">
              <div className="w-full max-w-md">
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-slate-600 rounded-3xl blur-2xl opacity-30"></div>

                  <form
                    onSubmit={handleSubmit}
                    className="relative bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 p-8 lg:p-10 space-y-6"
                  >
                    <div className="space-y-2">
                      <h2 className="text-3xl font-bold text-slate-900">
                        Welcome back
                      </h2>
                      <p className="text-slate-500">
                        Sign in to access your admin dashboard
                      </p>
                    </div>

                    {error && (
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-red-50 to-red-100 border border-red-200 p-4">
                        <div className="absolute inset-0 bg-red-500/5"></div>
                        <p className="relative text-sm text-red-700 font-medium">{error}</p>
                      </div>
                    )}

                    <div className="space-y-5">
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Email Address
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <FiMail className="w-5 h-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                          </div>
                          <input
                            type="email"
                            required
                            placeholder="test@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl pl-12 pr-4 py-3.5 text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Password
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <FiLock className="w-5 h-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                          </div>
                          <input
                            type={showPassword ? "text" : "password"}
                            required
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl pl-12 pr-12 py-3.5 text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-blue-600 transition-colors"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-blue-700 to-slate-800 py-4 text-base font-bold text-white shadow-lg shadow-blue-900/30 transition-all hover:shadow-xl hover:shadow-blue-900/50 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                      <span className="relative flex items-center justify-center gap-2">
                        {loading ? (
                          <>
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Signing in...</span>
                          </>
                        ) : (
                          <>
                            <span>Sign in to Dashboard</span>
                            <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </span>
                    </button>

                    <div className="pt-4 text-center">
                      <p className="text-xs text-slate-400">
                        © {new Date().getFullYear()} Expo pilot. All rights reserved.
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;